// Email template constants
export const EMAIL_TEMPLATES = {
  BUG_REPORT: 'BUG_REPORT',
  WELCOME: 'WELCOME', 
  PASSWORD_RESET: 'PASSWORD_RESET',
  SUBSCRIPTION_EXPIRING: 'SUBSCRIPTION_EXPIRING',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  SURVEY_EXPORT: 'SURVEY_EXPORT',
  VERIFICATION: 'VERIFICATION',
  SURVEY_CREATED: 'SURVEY_CREATED',
  TEAM_INVITE: 'TEAM_INVITE',
  ROLE_UPDATED: 'ROLE_UPDATED',
  TEAM_REMOVAL: 'TEAM_REMOVAL',
  SURVEY_COMPLETED: 'SURVEY_COMPLETED',
  SURVEY_SHARED: 'SURVEY_SHARED',
  EXPORT_READY: 'EXPORT_READY',
  MAINTENANCE_NOTICE: 'MAINTENANCE_NOTICE',
  BETA_APPROVAL_REQUEST: 'BETA_APPROVAL_REQUEST',
  BETA_APPROVAL_APPROVED: 'BETA_APPROVAL_APPROVED',
  BETA_APPROVAL_REJECTED: 'BETA_APPROVAL_REJECTED',
  BETA_TRIAL_EXPIRED: 'BETA_TRIAL_EXPIRED',
  BETA_TRIAL_EXTENDED: 'BETA_TRIAL_EXTENDED',
  CUSTOM_PLAN_REQUEST: 'CUSTOM_PLAN_REQUEST'
};

// Email templates
const templates = {
  [EMAIL_TEMPLATES.VERIFICATION]: {
    subject: 'Verify Your Email',
    html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
      <h1 style="color: #EF4444; text-align: center;">Verify Your Email</h1>
      <p>Please use the following verification code to complete your signup:</p>
      <div style="text-align: center; margin: 30px 0;">
        <div style="background-color: #F3F4F6; padding: 20px; border-radius: 8px; font-size: 24px; font-weight: bold; letter-spacing: 4px;">
          {{code}}
        </div>
      </div>
      <p>This code will expire in {{expiryTime}}.</p>
      <p style="color: #6B7280; font-size: 14px;">If you didn't request this verification code, please ignore this email.</p>
    </div>`,
    text: `
Verify Your Email

Please use the following verification code to complete your signup:

{{code}}

This code will expire in {{expiryTime}}.

If you didn't request this verification code, please ignore this email.`
  },
  [EMAIL_TEMPLATES.BETA_APPROVAL_REQUEST]: {
    subject: 'New Beta User Approval Required',
    body: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #EF4444; text-align: center;">New Beta Access Request</h1>
        <div style="background-color: #F3F4F6; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h2 style="margin-top: 0;">User Details:</h2>
          <ul style="list-style: none; padding: 0;">
            <li><strong>Name:</strong> {{userName}}</li>
            <li><strong>Email:</strong> {{userEmail}}</li>
            <li><strong>Company:</strong> {{company}}</li>
            <li><strong>Role:</strong> {{role}}</li>
          </ul>
        </div>
        <div style="text-align: center; margin: 30px 0;">
          <a href="{{approvalUrl}}" style="background-color: #EF4444; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">
            Review Request
          </a>
        </div>
      </div>
    `
  },
  [EMAIL_TEMPLATES.BETA_APPROVAL_APPROVED]: {
    subject: 'Beta Access Approved',
    body: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #EF4444; text-align: center;">Beta Access Approved!</h1>
        <p>Your beta access request has been approved. You now have 14 days of access to test all features.</p>
        <p>The trial period will begin when you first log in.</p>
        <p>Please sign in to get started:</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="{{loginUrl}}" style="background-color: #EF4444; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">
            Sign In Now
          </a>
        </div>
      </div>
    `
  },
  [EMAIL_TEMPLATES.BETA_TRIAL_EXPIRED]: {
    subject: 'Beta Trial Period Ended',
    body: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #EF4444; text-align: center;">Beta Trial Ended</h1>
        <p>Thank you for participating in our beta program! Your 14-day trial has now ended.</p>
        <p>Would you like to continue testing? Submit your feedback about your experience to request an additional 14 days of access.</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="{{feedbackUrl}}" style="background-color: #EF4444; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">
            Submit Feedback
          </a>
        </div>
      </div>
    `
  },
  [EMAIL_TEMPLATES.BETA_TRIAL_EXTENDED]: {
    subject: 'Beta Access Extended',
    body: `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h1 style="color: #EF4444; text-align: center;">Beta Access Extended!</h1>
        <p>Thank you for your valuable feedback! Your beta access has been extended for another 14 days.</p>
        <p>Your new trial end date is: {{newEndDate}}</p>
        <div style="text-align: center; margin: 30px 0;">
          <a href="{{loginUrl}}" style="background-color: #EF4444; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px;">
            Continue Testing
          </a>
        </div>
      </div>
    `
  }
};

// Template validation helper
const validateTemplate = (template, templateId) => {
  if (!template) {
    throw new Error(`Template ${templateId} is undefined`);
  }
  if (!template.subject) {
    throw new Error(`Template ${templateId} is missing subject`);
  }
  if (!template.html && !template.body) {
    throw new Error(`Template ${templateId} is missing html or body`);
  }
  return template;
};

export const getEmailTemplate = (templateId, variables) => {
  const template = templates[templateId];
  if (!template) return null;
  
  try {
    validateTemplate(template, templateId);
    
    // Replace variables in both HTML and text templates
    let { subject, html, text } = template;
    Object.entries(variables || {}).forEach(([key, value]) => {
      const regex = new RegExp(`{{${key}}}`, 'g');
      subject = subject.replace(regex, value || '');
      if (html) html = html.replace(regex, value || '');
      if (text) text = text.replace(regex, value || '');
    });

    return { subject, html, text };
  } catch (error) {
    logError('Template error:', error);
    throw error;
  }
};

// Export template validation for testing
export const validateTemplates = () => {
  const templateEntries = Object.entries(EMAIL_TEMPLATES);
  const errors = [];

  templateEntries.forEach(([id, templateId]) => {
    try {
      const template = getEmailTemplate(templateId, {});
      if (!template) {
        errors.push(`Template ${id} (${templateId}) is not defined`);
      }
    } catch (error) {
      errors.push(`Template ${id} (${templateId}): ${error.message}`);
    }
  });

  return errors;
};